/**
 * Shared utilities for dynamic entity display across the signal/filter system.
 *
 * Object types are workspace-specific and can be enabled/disabled per workspace.
 * This module provides helpers to resolve display labels, sort defaults, and
 * field-name prefixes for any object type — not just accounts/users.
 */

type WorkspaceObject = {
  api_slug?: string;
  singular_noun?: string;
  plural_noun?: string;
  enabled?: boolean;
};

type WorkspaceSchema = {
  objects?: WorkspaceObject[];
  objectsBySlug?: Record<string, WorkspaceObject>;
};

/**
 * Resolve a human-readable singular label for an entity slug.
 * Uses the workspace schema when available, otherwise falls back to
 * well-known defaults and finally title-cases the slug itself.
 */
const WELL_KNOWN_LABELS: Record<string, { singular: string; plural: string }> = {
  accounts:               { singular: "Account",      plural: "Accounts" },
  users:                  { singular: "User",         plural: "Users" },
  contacts:               { singular: "Contact",      plural: "Contacts" },
  companies:              { singular: "Company",      plural: "Companies" },
  meetings:               { singular: "Meeting",      plural: "Meetings" },
  docs:                   { singular: "Document",     plural: "Documents" },
  conversations:          { singular: "Conversation", plural: "Conversations" },
  conversation_messages:  { singular: "Message",      plural: "Messages" },
  deals:                  { singular: "Deal",         plural: "Deals" },
  tickets:                { singular: "Ticket",       plural: "Tickets" },
  tasks:                  { singular: "Task",         plural: "Tasks" },
};

const titleCase = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const getEntityLabel = (
  slug: string,
  workspaceSchema?: WorkspaceSchema,
  form: "singular" | "plural" = "singular",
): string => {
  // Try workspace schema first
  const obj = workspaceSchema?.objectsBySlug?.[slug];
  if (obj) {
    if (form === "plural") return obj.plural_noun || obj.singular_noun || titleCase(slug);
    return obj.singular_noun || titleCase(slug);
  }
  // Well-known fallback
  const known = WELL_KNOWN_LABELS[slug];
  if (known) return form === "plural" ? known.plural : known.singular;
  // Last resort
  return titleCase(slug);
};

/**
 * Get the default sort field for an entity type.
 * Accounts/users have `last_seen`; custom objects fall back to `created_at`.
 */
export const getDefaultSortField = (entitySlug: string): string => {
  if (entitySlug === "accounts" || entitySlug === "users") {
    return `${entitySlug}.last_seen`;
  }
  return `${entitySlug}.created_at`;
};

/**
 * Build entity options for a <Select> from the workspace schema.
 * Falls back to accounts + users when no schema is available.
 */
export const buildEntityOptions = (
  workspaceSchema?: WorkspaceSchema,
): Array<{ value: string; label: string }> => {
  if (!workspaceSchema?.objects?.length) {
    return [
      { value: "accounts", label: "Account" },
      { value: "users", label: "User" },
    ];
  }
  return workspaceSchema.objects
    .filter((obj) => obj.enabled)
    .map((obj) => {
      const slug = (obj.api_slug || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
      const normalizedSlug = slug === "contacts" ? "users" : slug;
      const rawLabel = obj.singular_noun || normalizedSlug;
      return {
        value: normalizedSlug,
        label: titleCase(rawLabel),
      };
    })
    .filter((opt) => opt.value);
};
